
















































































































































import { Component, Inject, Mixins, Watch } from "vue-property-decorator";
import { ValidationObserver } from "vee-validate";
import _ from "@/boot/lodash";

// Components
import ProductConfigProvider from "@/components/app/global/baskets/basket/productConfigProvider";

// Mixins
import HasValidation from "@/mixins/HasValidation";

// Enums/Data
import { QUERY_PARAMS } from "@/data/constants";
import { DeferModes } from "@/data/enums/provisioning";

// Types
import {
  IBasketProviderData,
  IBasketProviderMethods
} from "@/models/providers/baskets";
import {
  IProvisionProviderData,
  IProvisionProviderMethods
} from "@/models/providers/provisioning";
import { IBasketProvisioningFieldsForm } from "@/models/provisioning";
import { IShopProviderMethods } from "@/models/providers/basket/shop";
import { IProductConfig } from "@/models/providers/products";
import { IProduct } from "@/models/products";

@Component({
  components: {
    "shop-product-provider": () =>
      import(
        "@/components/app/global/baskets/basket/productView/shopProductProvider.vue"
      ),
    "product-config-provider": ProductConfigProvider,
    "product-provision-provider": () =>
      import(
        "@/components/app/global/baskets/basket/provision/productProvisionProvider.vue"
      ),
    "product-attributes": () =>
      import(
        "@/components/app/global/baskets/basket/productView/productAttributes.vue"
      ),
    "product-breadcrumbs": () =>
      import(
        "@/components/app/global/baskets/basket/productView/productBreadcrumbs.vue"
      ),
    "product-config-link": () =>
      import(
        "@/components/app/global/baskets/basket/productView/productConfigLink.vue"
      ),
    "product-control": () =>
      import(
        "@/components/app/global/baskets/basket/productView/productControl.vue"
      ),
    "product-details": () =>
      import(
        "@/components/app/global/baskets/basket/productView/productDetails.vue"
      ),
    "product-trial": () =>
      import(
        "@/components/app/global/baskets/basket/productView/productTrial.vue"
      ),
    "product-options": () =>
      import(
        "@/components/app/global/baskets/basket/productView/productOptions.vue"
      ),
    "product-provision-fields": () =>
      import(
        "@/components/app/global/baskets/basket/provision/productProvisionFields.vue"
      ),
    "product-terms": () =>
      import(
        "@/components/app/global/baskets/basket/productView/productTerms.vue"
      )
  }
})
export default class BasketProductView extends Mixins(HasValidation) {
  // Refs

  public declare $refs: {
    productConfigProvider: ProductConfigProvider;
    observer: InstanceType<typeof ValidationObserver>;
  };

  // Inject - Basket Provider Data

  @Inject({ from: "basketProviderData" })
  public readonly $basketProviderData!: () => IBasketProviderData;

  public get $basketData() {
    return this.$basketProviderData();
  }

  // Inject - Basket Provider Methods

  @Inject({ from: "basketProviderMethods" })
  public readonly $basketMethods!: IBasketProviderMethods;

  // Inject - Shop Provider Methods

  @Inject({ from: "shopProviderMethods" })
  public readonly $shopMethods!: IShopProviderMethods;

  // Inject - Provision Provider Data

  @Inject({ from: "provisionProviderData" })
  public readonly $provisionData!: () => IProvisionProviderData;

  // Inject - Provision Provider Methods

  @Inject({ from: "provisionProviderMethods" })
  public readonly $provisionMethods!: IProvisionProviderMethods;

  // Data

  public isLoading = true;
  public isReloading = false;
  public isProcessing = false;

  public requestError = false;
  public productId = "";
  public basketProductId = "";

  public config: IProductConfig | null = null;
  public configClone: IProductConfig | null = null;
  public configIsInitialised = false;
  public deferModes = DeferModes;

  public combinedProduct: {
    product: IProduct;
    sld: string;
  } | null = null;

  public form = {
    provision_field_values: {}
  } as IBasketProvisioningFieldsForm;
  public formClone = {
    provision_field_values: {}
  } as IBasketProvisioningFieldsForm;
  public initialProductForm = true;

  public useTrialVersion = false;

  // Computed

  public get basketProduct() {
    const bpid = _.get(this.$route.query, QUERY_PARAMS.BASKET_PRODUCT_ID, null);
    const products = _.get(this.$basketData, "basket.products");
    return _.find(products, ["id", bpid]) || null;
  }

  // Life-cycle Methods

  public async created() {
    this.initialiseProductPage();

    this.$bus.$on("basketUpdateCombinedProduct", product => {
      this.combinedProduct = product;
    });
  }

  public beforeDestroy() {
    this.$bus.$off("basketUpdateCombinedProduct");
  }

  // Methods

  public initialiseProductPage() {
    // Set basketProductId
    this.setBasketProductId();

    // Set productId
    this.setProductId();

    // Clear additionalRequiredProduct
    this.combinedProduct = null;

    if (this.basketProduct) {
      this.initialProductForm = false;
    }

    if (!this.productId) return this.goToShop();
  }

  public handleError(error) {
    if (
      this.$store.getters["api/is403"](error) ||
      this.$store.getters["api/is404"](error) ||
      this.$store.getters["api/is409"](error)
    ) {
      this.goToShop();
    }
    if (this.$store.getters["api/is401"](error)) {
      this.$basketMethods.forceBasketReset();
    }
  }

  public handleValidationError(error) {
    setTimeout(() => {
      this.$store.dispatch("api/handleValidationError", {
        error,
        vm: this,
        showToast: false
      });
    }, 250);
  }

  public setBasketProductId(id = 0) {
    this.$set(
      this,
      "basketProductId",
      id || _.get(this.basketProduct, "id", id)
    );
  }

  public setProductId() {
    this.$set(
      this,
      "productId",
      _.get(
        this.basketProduct,
        "product_id",
        _.get(this.$route.query, QUERY_PARAMS.PRODUCT_ID)
      )
    );
  }

  public async validateBeforeSubmit() {
    if (!this.config) return;

    try {
      this.isProcessing = true;

      if (!(await this.validate()))
        throw this.$t("_sentence.form_is_not_valid");

      // Add or update product
      const basket = !this.basketProductId
        ? await this.$basketMethods.addToBasket({
            ...this.config,
            start_trial: this.useTrialVersion
          })
        : this.mainFormIsChanged
        ? await this.$basketMethods.updateBasketProduct(this.basketProductId, {
            ...this.config,
            start_trial: this.useTrialVersion
          })
        : this.$basketData.basket;

      const showCrossSellModal =
        !this.basketProductId && !this.$basketData.routeCrossSell;
      // Get newly created basket product
      const basketProductId =
        this.basketProductId ||
        _.get(_.orderBy(basket.products, ["created_at"], ["desc"])[0], "id");

      if (!this.basketProductId && !this.combinedProduct) {
        // Set basket product id
        this.setBasketProductId(basketProductId);
        // Silently route to self (appending 'bpid=xxx' to query)
        await this.routeToSelf(basketProductId, basket.id);
      }

      // Save provisioning fields (if applicable)
      if (this.hasProvisioning && this.provisionFormIsChanged) {
        await this.$provisionMethods.updateBasketProductFields(
          basket.id,
          basketProductId,
          this.form
        );

        await this.$provisionMethods.removeBasketProductValues({
          basketId: basket.id,
          productId: basketProductId
        });

        // Refresh basket data (unless combined product about to be added)
        if (!this.combinedProduct)
          await this.$basketMethods.getBasket(true, basket.id);
      }

      if (this.combinedProduct) {
        // Add combined product
        await this.addCombinedProduct(
          showCrossSellModal,
          this.productId,
          basketProductId
        );
        // Refresh basket data and exit
        return this.$basketMethods.getBasket(true, basket.id);
      }

      if (showCrossSellModal) {
        this.$basketMethods.showCrossSellsModal({
          productId: this.productId,
          basketId: basket.id,
          isProvisionable: this.$provisionMethods.isProvisionable
        });
      }

      // Route to basket view
      this.$shopMethods.goToBasket();
    } catch (error) {
      this.$store.dispatch("api/handleValidationError", {
        error,
        vm: this
      });
    } finally {
      this.isProcessing = false;
    }
  }

  public async routeToSelf(bpid, bid) {
    try {
      await this.$router.replace({
        query: {
          ..._.omitBy(
            _.omit(this.$route.query, [
              QUERY_PARAMS.PRODUCT_ID,
              QUERY_PARAMS.CATEGORY_ID,
              QUERY_PARAMS.COUPONS,
              QUERY_PARAMS.CROSS_SELL_PRODUCT,
              QUERY_PARAMS.READ_MORE,
              QUERY_PARAMS.SEARCH,
              QUERY_PARAMS.SUBPRODUCT_IDS
            ]),
            (queryValue, queryKey) =>
              queryKey.includes(QUERY_PARAMS.PRODUCT_FIELDS)
          ),
          [QUERY_PARAMS.BASKET_PRODUCT_ID]: bpid
        },
        params: {
          basketId: bid
        }
      });
    } catch {
      // Silent catch
    }
  }

  public goToShop() {
    return this.$shopMethods.goToShop(
      _.pick(this.$route.query, [
        QUERY_PARAMS.CURRENCY_CODE,
        QUERY_PARAMS.CATEGORY_ID,
        QUERY_PARAMS.COUPONS
      ])
    );
  }

  public initialiseForm() {
    this.formClone = _.cloneDeep(this.form);

    // Handle error form previous 422
    const previousProvisionValues =
      this.$provisionData().savedBasketProductValues[
        `${this.$basketData.basketId}_${this.basketProductId}`
      ] || {};

    if (_.isEmpty(previousProvisionValues)) return;

    this.handleValidationError(previousProvisionValues.error);
  }

  public updateForm(values) {
    if (!_.isEmpty(this.form.provision_field_values)) return;

    this.$set(this, "form", { provision_field_values: values });
  }

  public get hasProvisioning() {
    return this.$provisionData().hasProvisioning;
  }

  public get provisionFormIsChanged() {
    return !_.isEqual(
      this.form.provision_field_values,
      this.formClone.provision_field_values
    );
  }

  public get routeProductId() {
    return _.get(this.$route.query, QUERY_PARAMS.PRODUCT_ID, null);
  }

  public get subProductIds() {
    return _.map(
      _.concat(
        _.get(this.config, "attributes", []),
        _.get(this.config, "options", [])
      ),
      (item: any) => item.product_id
    );
  }

  public get mainFormIsChanged() {
    return (
      !_.isEqual(this.configClone, this.config) ||
      (this.basketProduct &&
        this.useTrialVersion !== this.basketProduct.in_trial)
    );
  }

  public async addCombinedProduct(
    showCrossSellModal,
    productId,
    basketProductId
  ) {
    if (!this.combinedProduct) return;

    try {
      const basket = await this.$basketMethods.addSilentlyToBasket(
        this.combinedProduct.product
      );

      const basketCombinedProducId = _.get(
        _.orderBy(basket.products, ["created_at"], ["desc"])[0],
        "id"
      );

      await this.$provisionMethods.updateBasketProductFields(
        basket.id,
        basketCombinedProducId,
        {
          provision_field_values: {
            sld: this.combinedProduct.sld
          }
        },
        true
      );

      if (await this.checkConfiguration()) {
        if (showCrossSellModal) {
          this.$basketMethods.showCrossSellsModal({
            productId,
            isProvisionable: this.$provisionMethods.isProvisionable,
            pfields: [{ name: "sld", value: this.combinedProduct.sld }],
            basketId: basket.id
          });
        }

        await this.routeToSelf(basketProductId, basket.id);

        this.$shopMethods.goToBasket();
        return;
      }

      const currentQueryParams = this.$route.query;
      delete currentQueryParams["upm-dac-search"];
      delete currentQueryParams[QUERY_PARAMS.PRODUCT_ID];

      return this.$shopMethods.goToProductView({
        queryParams: {
          ...currentQueryParams,
          [QUERY_PARAMS.BASKET_PRODUCT_ID]: basketCombinedProducId
        }
      });
    } catch (error) {
      // Silently catch error
    }
  }

  public async checkConfiguration() {
    try {
      await this.$provisionMethods.checkProvisionConfiguration(
        this.$basketData.basketId
      );
      return true;
    } catch (error) {
      return false;
    }
  }

  @Watch("config", { deep: true })
  public emitNewConfig(value) {
    if (!_.isNull(value) && !this.configIsInitialised) {
      this.configClone = value;
      this.configIsInitialised = true;
    }
  }

  @Watch("basketProduct.id")
  public onBPIDchange() {
    this.initialiseProductPage();
  }

  @Watch("routeProductId")
  public onPIDchange() {
    this.initialiseProductPage();
  }
}
